
import { Button } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps, useLocation } from "react-router";
import { ReportActivitiesEnum, ReportStatesEnum, ReportStatusEnum, ReportTypeEnum } from "../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../constants/url-config";
import { BRResultDataModel, EcapReportModel, ORInputDataModel } from "../../../../models/ecap-report.model";
import { ReviewModel } from "../../../../models/review.model";
import { setStatus, setTitle } from "../../../../redux/actions/header.action";
import { ECAPService } from "../../../../services/ecap.service";
import { AMOUNT_UTIL } from "../../../../utils/amount.utils";
import { ROLE_GUARD } from "../../../../utils/role-guard";
import UTIL from "../../../../utils/utils";
import FooterAction from "../../../atoms/footer-action";
import AppReview from "../../../atoms/review";
import BROutPut from "../shared/br-output";
import InputTable from "../shared/input-table";
import OROutput from "../shared/or-output/or-output";
import OutPutTable from "../shared/output-table";
import Information from "./information";
import "./report-details.scss";


interface Props extends RouteChildrenProps<{ id: string }> { }


const EcapReportDetails = (props: Props) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const id = useRef<number>(0);
    const [data, setData] = useState<EcapReportModel>({});
    const location = useLocation();
    const [isApprovalWFL, setIsApprovalWFL] = useState(false);
    const [newReview, setNewReview] = useState<ReviewModel>();
    const [reviewData, setReviewData] = useState<ReviewModel[]>();
    const [orOutput, setOrOutput] = useState<ORInputDataModel>({});
    const [brOutput, setBrOutput] = useState<BRResultDataModel[]>([]);

    const getReportDetails = () => {
        if (id.current) {
            ECAPService.getEcapReportDetails(id.current, true).then(res => {
                setData(res.data);

                if (res.data.risk_type === ReportTypeEnum.Ramp) {
                    dispatch(setTitle(`FY${res.data.fiscal_year} - Quarter ${res.data.quarter}`));
                } else {
                    dispatch(setTitle(`FY${res.data.fiscal_year} - ${UTIL.getECapReportTypeDisplay(res.data.risk_type)}`));
                }

                updateStatus(res.data.submit_state);
                if (res.data.risk_type === ReportTypeEnum.OperationalRisk) {
                    inintOROutPut(res.data);
                } else if (res.data.risk_type === ReportTypeEnum.BusinessRisk) {
                    inintBROutPut(res.data);
                }
            })
        }
    }

    const inintBROutPut = (data: any) => {
        if (!data.result_data || !data.result_data.length) {
            return;
        }

        setBrOutput([{
            Name: t("EcapModule.lbDesiredRatingLevel"),
            Values: data.result_data[0].target_rating
        },
        {
            Name: t("EcapModule.lbCapitalRequired"),
            Values: AMOUNT_UTIL.convertNumericToFormattedAmount(data.result_data[0].capital_required)
        }]);
    }


    const downloadURI = (uri?: string, name: string = "") => {
        if (!uri) {
            return;
        }
        var link = document.createElement("a");
        link.setAttribute('download', name);
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    const updateStatus = (submit_state: string | undefined) => {
        const dom = UTIL.getLossStatesDOM(submit_state);
        const status = <span className="text-status" style={{ color: dom.color, backgroundColor: dom.bgColor }}>
            {dom.text}
        </span>

        dispatch(setStatus(status));
    }

    const comment = (message: string) => {
        ECAPService.createEcapCommentAsync(id.current, message).then(res => {
        })
    }

    const approve = () => {
        ECAPService.approveOrReferEcapReportAsync(id.current, ReportStatusEnum.Approved).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Approved;
                updateStatus(ReportStatesEnum.Approved);
                return pre;
            })

            setNewReview({ action: ReportActivitiesEnum.Approve })
        });
    }

    const refer = () => {
        ECAPService.approveOrReferEcapReportAsync(id.current, ReportStatusEnum.Referred).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Rejected;
                updateStatus(ReportStatesEnum.Rejected);
                return pre;
            })
            setNewReview({ action: ReportActivitiesEnum.Refer })
        });
    }

    const inintOROutPut = (data: any) => {
        if (!data.result_data || !data.result_data.length) {
            return;
        }

        const result_data = data.result_data[0] as ORInputDataModel;

        result_data.risk_weighted_equivalents?.map(item => {

            if (data.input_data && data.input_data.length) {
                const input_data = data.input_data[0] as ORInputDataModel;
                const net = input_data.net_interest_incomes?.find(p => p.year === item.year);
                if (net) {
                    item.net_interest_income = net?.value;
                }
            }

            return item;
        })

        setOrOutput(result_data);
    }

    const renderResult = () => {
        switch (data.risk_type) {
            case ReportTypeEnum.OperationalRisk:
                return <OROutput orOutput={orOutput} />
            case ReportTypeEnum.BusinessRisk:
                return <BROutPut brOutput={brOutput} created_on={data.created_on} />
            default:
                return <React.Fragment>

                    {
                        data.result_data && <div className="section-wrapper">
                            <h4>
                                {t("RapmReport.lbOutputData")}
                            </h4>

                            {
                                data.result_data.reverse().map((item, index) => (

                                    <React.Fragment key={index}>
                                        {
                                            (item.data && Array.isArray(item.data)) &&
                                            <div className="table-wrapper" >
                                                <p className="table-des">
                                                    {item.type_desc}
                                                </p>
                                                <OutPutTable
                                                    data={item.data}
                                                    type={data.risk_type} columns={item.columns}
                                                    table_code={item.type_code}
                                                />

                                                {
                                                    (data.download_result_links && data.download_result_links[item.type_code]) &&
                                                    <div className="action">
                                                        <Button type="primary" shape="round" onClick={() => downloadURI(data.download_result_links[item.type_code])}>
                                                            {t("RapmReport.btnDownload")}
                                                        </Button>
                                                    </div>
                                                }

                                            </div>
                                        }
                                    </React.Fragment>

                                ))
                            }
                        </div>
                    }

                    {
                        data.input_data && <div className="section-wrapper">
                            <h4>
                                {t("RapmReport.lbInputData")}
                            </h4>

                            {
                                data.input_data.map((item, index) => (

                                    <React.Fragment key={index}>
                                        {
                                            (item.data && Array.isArray(item.data)) &&
                                            <div className="table-wrapper">
                                                <p className="table-des">
                                                    {item.type_desc}
                                                </p>
                                                <InputTable data={item.data} />
                                                {
                                                    (data.download_input_links && data.download_input_links[item.type_code]) &&
                                                    <div className="action">
                                                        <Button type="primary" shape="round" onClick={() => downloadURI(data.download_input_links[item.type_code])}>
                                                            {t("RapmReport.btnDownload")}
                                                        </Button>
                                                    </div>
                                                }

                                            </div>
                                        }
                                    </React.Fragment>

                                ))
                            }
                        </div>
                    }

                </React.Fragment>
        }
    }

    useEffect(() => {
        setIsApprovalWFL(location.pathname.includes(ROUTE_PATHS.ApprovalWorkflowECAPModule) || location.pathname.includes(ROUTE_PATHS.ApprovalWorkflowPortfolioRAROC));
        id.current = Number(props.match?.params.id);
        getReportDetails();

        ECAPService.getEcapCommentsAsync(id.current).then(res => {
            setReviewData(res.data);
        });
        return (() => {
            dispatch(setTitle(""));
            dispatch(setStatus(""));
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="rapm-report-details-wrapper">

            <div className="section-wrapper">
                <h4>
                    {t("EcapModule.lbInformation")}
                </h4>

                <Information data={data} />
            </div>

            {
                renderResult()
            }

            {UTIL.CheckShowReviewByState(data.submit_state) &&
                <AppReview newReview={newReview} comment={comment} data={reviewData} showInputComment={ROLE_GUARD.hasApproverRole()} />}

            {data.submit_state === ReportStatesEnum.Submitted && ROLE_GUARD.hasApproverRole() && isApprovalWFL && <FooterAction onApprove={approve} onRefer={refer} />}

        </div>
    )
}

export default EcapReportDetails;